import { ATTENDANCE_BRANCHES } from "./attendanceBranches";

/**
 * Canonical branch values for orders / pending orders / reports / payroll (same as stored on documents).
 * Admins always see all branches; other users may be restricted via `users.allowedBranches`.
 */

/** Last outlet for credit (Udhar) rows from Billing + default scope on Credit / Due hub. */
export const BILLING_CREDIT_OUTLET_STORAGE_KEY = "billing_credit_outlet_branch";

export const APP_BRANCHES = [
  { value: "Main", label: "Main" },
  { value: "Branch 2", label: "Spareway" },
  { value: "Branch 3", label: "Branch" },
];

export const APP_BRANCH_VALUES = APP_BRANCHES.map((b) => b.value);

/** Normalize raw branch strings from Firestore to canonical values (matches order workflow). */
export function normalizeBranchValue(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "Main";
  if (s === "main" || s.includes("main")) return "Main";
  /** Legacy / export naming: "Branch 1" is the main shop in this dataset (maps to canonical `Main`). */
  if (s === "branch 1" || s === "branch1") return "Main";
  if (s === "spareway" || s.includes("branch 2") || s === "branch2") return "Branch 2";
  if (s === "branch 3" || s === "branch" || s === "branch3") return "Branch 3";
  if (s === "wholesale" || s.includes("wholesale")) return "Wholesale";
  return String(v ?? "Main");
}

const ALLOWED = new Set(APP_BRANCH_VALUES);

/**
 * @param {unknown} arr
 * @returns {string[] | null} null if input invalid; empty array = no restriction stored yet (treat as all)
 */
export function sanitizeAllowedBranchesArray(arr) {
  if (!Array.isArray(arr)) return null;
  const out = [
    ...new Set(
      arr
        .filter((x) => typeof x === "string")
        .map((x) => normalizeBranchValue(x))
        .filter((x) => ALLOWED.has(x))
    ),
  ];
  return out;
}

/**
 * @param {{ role?: string, allowedBranches?: unknown }} user — e.g. localStorage user or Firestore row
 * @returns {string[]} Effective branch values this user may use (subset of APP_BRANCH_VALUES)
 */
export function getEffectiveBranchValues(user) {
  if (!user) return [...APP_BRANCH_VALUES];
  const r = String(user.role || "").toLowerCase();
  if (r === "admin" || r === "administrator") return [...APP_BRANCH_VALUES];
  const raw = user.allowedBranches;
  if (raw == null) return [...APP_BRANCH_VALUES];
  if (Array.isArray(raw) && raw.length === 0) return [...APP_BRANCH_VALUES];
  const cleaned = sanitizeAllowedBranchesArray(raw);
  if (!cleaned || cleaned.length === 0) return [...APP_BRANCH_VALUES];
  return cleaned;
}

/**
 * Branch rows for dropdowns/tabs (labels preserved from APP_BRANCHES).
 * @param {{ role?: string, allowedBranches?: unknown }} user
 */
export function getVisibleAppBranches(user) {
  const eff = new Set(getEffectiveBranchValues(user));
  return APP_BRANCHES.filter((b) => eff.has(b.value));
}

export function canAccessAppBranch(user, branchValue) {
  const v = normalizeBranchValue(branchValue);
  return getEffectiveBranchValues(user).includes(v);
}

/** Maps `allowedBranches` values to payroll UI labels (`ATTENDANCE_BRANCHES[].label`). */
export const APP_BRANCH_VALUE_TO_ATTENDANCE_LABEL = {
  Main: "Main Shop",
  "Branch 2": "Spareway",
  "Branch 3": "Branch 3",
};

/**
 * Rows from {@link ATTENDANCE_BRANCHES} visible for payroll (Monitor, Monthly report, daily hours, employees), matching Pending Orders / Order Reports.
 * @param {{ role?: string, allowedBranches?: unknown }} user
 */
export function getVisibleAttendanceBranches(user) {
  const eff = new Set(getEffectiveBranchValues(user));
  const labels = new Set(
    APP_BRANCH_VALUES.filter((v) => eff.has(v)).map((v) => APP_BRANCH_VALUE_TO_ATTENDANCE_LABEL[v])
  );
  return ATTENDANCE_BRANCHES.filter((b) => labels.has(b.label));
}
